import { useState } from "react";
import { Play } from "lucide-react";

const VideoPlayer = ({ videoUrl, videoId: passedVideoId, mp4Url, posterUrl, title, description, thumbnail, startTime = 0 }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  // Direct mp4 mode (no YouTube parsing needed)
  if (mp4Url) {
    return (
      <div className="relative group">
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-500/20">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <video
              src={mp4Url}
              poster={posterUrl}
              controls
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full bg-black"
              data-testid="video-mp4"
            >
              Your browser does not support the video tag.
            </video>
          </div>
          {(title || description) && (
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-slate-950/90 via-slate-950/40 to-transparent p-6 pointer-events-none">
              {title && <h4 className="text-white font-semibold text-lg mb-1">{title}</h4>}
              {description && <p className="text-gray-300 text-sm">{description}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Extract video ID from YouTube URL or use passed videoId
  const getVideoId = (url) => {
    if (!url) return '';
    const embedMatch = url.match(/embed\/([^?]+)/);
    if (embedMatch) return embedMatch[1];
    const watchMatch = url.match(/watch\?v=([^&]+)/);
    if (watchMatch) return watchMatch[1];
    const shortMatch = url.match(/youtu\.be\/([^?]+)/);
    if (shortMatch) return shortMatch[1];
    return '';
  };

  const videoId = passedVideoId || getVideoId(videoUrl);
  const embedUrl = `https://www.youtube.com/embed/${videoId}`;
  const thumbnailUrl = thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  if (!videoId) return null;

  return (
    <div className="relative group">
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-500/20">
        {/* Video Container */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          {isPlaying ? (
            // Show YouTube iframe when playing
            <iframe
              src={`${embedUrl}?autoplay=1&rel=0&modestbranding=1&start=${startTime}`}
              title={title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          ) : (
            // Show thumbnail with custom play button before playing
            <div 
              className="absolute inset-0 cursor-pointer"
              onClick={handlePlay}
            >
              <img 
                src={thumbnailUrl}
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to hqdefault if maxresdefault doesn't exist
                  e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                }}
              />
              {/* Dark overlay - lighter for better visibility */}
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors"></div>
              
              {/* Custom Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-purple-600/70 backdrop-blur-sm flex items-center justify-center group-hover:bg-purple-500 group-hover:scale-110 transition-all duration-300 shadow-lg shadow-purple-500/30">
                  <Play className="w-6 h-6 text-white ml-1" fill="white" />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Video Info Overlay - Hide when playing */}
        {(title || description) && !isPlaying && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent p-6 pointer-events-none">
            {title && (
              <h4 className="text-white font-semibold text-lg mb-2">{title}</h4>
            )}
            {description && (
              <p className="text-gray-300 text-sm">{description}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
